import { TextStyleSkeleton } from "@/components/skeletons/TextStyleSkeleton";
import { useSlideEditorContext } from "@/components/slides/editor/SlideEditorContext";
import { nonDraggablesTypeSlide } from "@/lib/constants/slides";
import { Dnd } from "@/lib/dnd/components/Dnd";
import { useAddTextElement } from "@/lib/hooks/slides/editor/useAddTextElement";
import { useGetTextStyles } from "@/lib/hooks/slides/editor/useGetTextStyles";
import { useTextStyle } from "@/lib/hooks/slides/editor/useTextStyle";
import { useAppSelector } from "@/store/hooks";
import { Button, IconButton, LoadMoreButton, Modal } from "@ai-teach/ui-web";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { RiDeleteBinFill, RiFontSizeAi } from "react-icons/ri";
import { useInView } from "react-intersection-observer";
import { DisabledLockOverlay } from "./DisabledLockOverlay";

export function TextPanel() {
  const { setActivePanel } = useSlideEditorContext();
  const [isCreateStyleModalOpen, setIsCreateStyleModalOpen] = useState<boolean>(false);

  const { slide, selectedId } = useAppSelector((state) => state.slideEditor.present);

  const textsDisabled = slide
    ? (nonDraggablesTypeSlide[slide?.type] as readonly string[]).includes(slide.knowledgeArea)
    : true;

  const { handleAddText } = useAddTextElement();
  const {
    textStyles,
    loadMore: loadMoreTextStyles,
    hasMore: hasMoreTextStyles,
    loading: loadingTextStyles,
    TEXT_STYLES_LIMIT,
  } = useGetTextStyles({
    searchText: undefined,
    skip: textsDisabled,
  });

  // Infinite scroll trigger
  const { ref: loadMoreRef, inView } = useInView({
    threshold: 0,
    triggerOnce: false,
  });

  useEffect(() => {
    if (hasMoreTextStyles && !loadingTextStyles && inView) {
      loadMoreTextStyles();
    }
  }, [hasMoreTextStyles, loadingTextStyles, inView, loadMoreTextStyles]);

  const {
    createTextStyle,
    handleApplyTextStyle,
    createTextStyleLoading,
    deleteTextStyle,
    deleteTextStyleLoading,
  } = useTextStyle();

  if (!slide) return null;

  const elements = slide.elements;

  const selectedElement = elements.find((el) => el.id === selectedId);
  const isTextElement = selectedElement?.type === "text";

  const textStyleNameRef = useRef<HTMLInputElement | null>(null);

  return (
    <div className="px-2">
      {/* Section header */}
      <div className="mb-4">
        <h3 className="text-sm font-semibold uppercase tracking-wider px-1">Default Text Styles</h3>
        <p className="text-xs text-slate-400 mt-1 px-1">Choose a text style to add</p>
      </div>

      {/* Text style buttons with visual preview */}
      <div className="relative">
        <div
          className={`flex flex-col gap-3 transition-all duration-300 ${
            textsDisabled ? "opacity-40 blur-[2px] pointer-events-none" : ""
          }`}
        >
          {/* Heading Button */}
          <Dnd
            id="heading"
            data={{ id: "heading", type: "text" }}
            className="w-full h-full flex items-center justify-center"
          >
            <IconButton
              onClick={() => {
                handleAddText("heading");
                setActivePanel(null);
              }}
              variant="ghost"
              className="group w-[100%] relative overflow-hidden rounded-xl bg-white border-2 border-slate-200 hover:border-primary-400 transition-all duration-200 shadow-sm hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 active:shadow-sm"
              aria-label="Add heading text"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-primary-50 to-accent-50 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />

              <div className="relative p-4 text-left">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Heading
                  </span>
                  <span className="text-[10px] text-slate-400 font-medium">48px</span>
                </div>
                <div className="text-2xl font-bold text-slate-800 group-hover:text-primary-900 transition-colors">
                  Add a heading
                </div>
              </div>
            </IconButton>
          </Dnd>
          {/* Subheading Button */}
          <Dnd
            id="subheading"
            data={{ id: "subheading", type: "text" }}
            className="w-full h-full flex items-center justify-center"
          >
            <IconButton
              onClick={() => {
                handleAddText("subheading");
                setActivePanel(null);
              }}
              variant="ghost"
              className="group w-[80%] relative overflow-hidden rounded-xl bg-white border-2 border-slate-200 hover:border-primary-400 transition-all duration-200 shadow-sm hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 active:shadow-sm"
              aria-label="Add subheading text"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-primary-50 to-accent-50 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />

              <div className="relative p-3.5 text-left">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                    Subheading
                  </span>
                  <span className="text-[10px] text-slate-400 font-medium">32px</span>
                </div>
                <div className="text-lg font-semibold text-slate-800 group-hover:text-primary-900 transition-colors">
                  Add a subheading
                </div>
              </div>
            </IconButton>
          </Dnd>
          {/* Body Text Button */}
          <Dnd
            id="body"
            data={{ id: "body", type: "text" }}
            className="w-full h-full flex items-center justify-center"
          >
            <IconButton
              onClick={() => {
                handleAddText("body");
                setActivePanel(null);
              }}
              variant="ghost"
              className="group w-[60%] relative overflow-hidden rounded-xl bg-white border-2 border-slate-200 hover:border-primary-400 transition-all duration-200 shadow-sm hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 active:shadow-sm"
              aria-label="Add body text"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-primary-50 to-accent-50 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />

              <div className="relative p-3 text-left">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                    Body
                  </span>
                  <span className="text-[9px] text-slate-400 font-medium">16px</span>
                </div>
                <div className="text-sm text-slate-700 group-hover:text-primary-900 transition-colors">
                  Add a body text
                </div>
              </div>
            </IconButton>
          </Dnd>
        </div>

        {textsDisabled && (
          <DisabledLockOverlay
            title="Text Not Available"
            description="This slide type doesn't support text elements"
          />
        )}
      </div>

      <div className="mt-8">
        <div className="mb-4 px-1 flex items-end justify-between">
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider">My Text Styles</h3>
            <p className="text-xs text-slate-400 mt-1">Your saved text styles</p>
          </div>
          <Button
            variant="primary"
            size="sm"
            disabled={textsDisabled || createTextStyleLoading}
            className={`group relative overflow-hidden rounded-lg shadow-sm transition-all duration-200
    bg-gradient-to-r from-primary-500 to-accent-500 text-white
    hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:shadow-sm
    ${textsDisabled ? "opacity-40 blur-[1px] pointer-events-none hover:translate-y-0 hover:shadow-sm" : ""}
  `}
            onClick={() => {
              if (!isTextElement) {
                toast.error("Select a text element", { id: "text-style-save-error" });
                return;
              }
              setIsCreateStyleModalOpen(true);
            }}
          >
            <span className="relative">Save Style</span>
          </Button>
        </div>

        <div className="relative mt-2">
          <div
            className={`grid grid-cols-2 gap-3 transition-all duration-300  ${
              textsDisabled ? "opacity-40 blur-[2px] pointer-events-none" : ""
            }`}
          >
            {/* Initial Loading State - only show skeleton when loading AND no data */}
            {loadingTextStyles && (!textStyles || textStyles.length === 0) && (
              <TextStyleSkeleton count={TEXT_STYLES_LIMIT} />
            )}

            {/* Empty State*/}
            {!loadingTextStyles && (!textStyles || textStyles.length === 0) && (
              <div className="relative col-span-full grid grid-cols-2 gap-3">
                {/* Skeleton background */}
                <TextStyleSkeleton />

                {/* Blur overlay with empty state message */}
                <div className="absolute inset-0 flex flex-col items-center justify-center backdrop-blur-sm bg-white/60">
                  <div className="relative mb-4">
                    <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-slate-100 to-slate-200 border-2 border-slate-200 flex items-center justify-center">
                      <RiFontSizeAi fontSize={30} className="text-slate-400" />
                    </div>
                  </div>
                  <h3 className="text-lg font-medium text-slate-700 mb-1">No text styles yet</h3>
                  <p className="text-sm text-slate-500 text-center max-w-sm">
                    Create your first text style to get started with custom typography
                  </p>
                </div>
              </div>
            )}

            {/* Render State - always show existing items, even while loading more */}
            {textStyles &&
              textStyles.length > 0 &&
              textStyles.map((style) => {
                return (
                  <Dnd
                    key={style.id}
                    id={`textStyle-${style.id}`}
                    data={{ id: style.id, type: "textStyle", data: style }}
                  >
                    <div
                      onClick={() => {
                        handleApplyTextStyle(style);
                        setActivePanel(null);
                      }}
                      className="group relative overflow-hidden rounded-xl bg-white hover:border-primary-400 transition-all duration-200 shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 p-4 h-[80px] flex items-center justify-center cursor-pointer"
                      aria-label={`Text style: ${style.name}`}
                    >
                      {/* Background pattern */}
                      <div className="absolute inset-0 opacity-5 group-hover:opacity-20 transition-opacity">
                        <div className="absolute inset-0 bg-gradient-to-tl from-primary to-accent-400" />
                      </div>

                      {/* Preview text with actual style */}
                      <div className="relative text-center break-words w-full">
                        <div
                          style={{
                            fontFamily: style.fontFamily || "inherit",
                            fontWeight: style.fontWeight || "normal",
                            fontSize: "16px",
                            color: style.color || "#000000",
                            textDecoration: style.textDecoration ?? undefined,
                            fontStyle: style.fontStyle ?? undefined,
                            border: `${style.strokeWidth}px ${style.lineStyle} ${style.stroke}`,
                            borderRadius: style.cornerRadius,
                            padding: 5,
                          }}
                          className="line-clamp-3"
                        >
                          {style.name}
                        </div>
                      </div>

                      {/* Delete Button */}
                      <IconButton
                        variant="danger"
                        size="sm"
                        shape="square"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteTextStyle(style.id);
                        }}
                        disabled={textsDisabled || deleteTextStyleLoading}
                        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 bg-white text-error transition-opacity duration-200 hover:scale-110"
                      >
                        <RiDeleteBinFill />
                      </IconButton>
                    </div>
                  </Dnd>
                );
              })}
          </div>

          {textsDisabled && (
            <DisabledLockOverlay
              title="Text Styles Not Available"
              description="This slide type doesn't support text styling"
            />
          )}
        </div>
      </div>

      {/* Infinite scroll trigger */}
      {hasMoreTextStyles && (
        <div ref={loadMoreRef} className="flex justify-center py-8">
          {loadingTextStyles && (
            <div className="flex items-center gap-2 text-muted">
              <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              <span>Loading more styles...</span>
            </div>
          )}
        </div>
      )}

      {/* Helpful tip */}
      <div className="mt-6 p-3 bg-primary-50/50 border border-primary-100 rounded-lg">
        <p className="text-xs text-primary-700 text-center">
          <span className="font-semibold">Tip:</span> Click your favorite text style to add it to
          your text
        </p>
      </div>

      {/* Create Text Style Modal */}
      <Modal
        isOpen={isCreateStyleModalOpen}
        onClose={() => {
          setIsCreateStyleModalOpen(false);
        }}
        title="Create Text Style"
      >
        <div className="space-y-6">
          <div>
            <label htmlFor="name" className="block label mb-2">
              <span className="text-red-500">*</span> Style Name
            </label>
            <input
              ref={textStyleNameRef}
              type="text"
              name="name"
              id="name"
              placeholder="Enter style name"
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-4">
            <Button
              variant="secondary"
              onClick={() => {
                setIsCreateStyleModalOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={async () => {
                await createTextStyle(textStyleNameRef.current?.value ?? "");
                setIsCreateStyleModalOpen(false);
              }}
            >
              Create
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
